import { ArrowDownLeft, ArrowUpRight, Copy, ExternalLink, X } from "lucide-react";
import type { AnalysisResult, GraphLink, GraphNode } from "../../server/types";
import { compactNumber, explorerAddress, explorerTransaction, relativeTime, shortAddress } from "../lib/format";

function endpointId(value: string | GraphNode): string {
  return typeof value === "string" ? value : value.id;
}

export function NodeDetail({ data, node, onClose }: { data: AnalysisResult; node: GraphNode; onClose: () => void }) {
  const links = data.links.filter((link) => {
    const source = endpointId(link.source as unknown as string | GraphNode);
    const target = endpointId(link.target as unknown as string | GraphNode);
    return source === node.id || target === node.id;
  });
  const byId = new Map(data.nodes.map((item) => [item.id, item]));

  return (
    <section className="node-detail">
      <button className="detail-close" onClick={onClose}><X /></button>
      <div className="detail-title">
        <i style={{ borderColor: node.color, background: node.color }} />
        <span><small>{node.rank ? `Holder #${node.rank}` : "Traced wallet"}</small><h3>{node.label || shortAddress(node.address, 8)}</h3></span>
      </div>
      <div className="address-line">
        <code>{node.address}</code>
        <button title="Copy address" onClick={() => navigator.clipboard.writeText(node.address)}><Copy /></button>
        <a title="Open in explorer" target="_blank" rel="noreferrer" href={explorerAddress(data.token.chain, node.address)}><ExternalLink /></a>
      </div>
      <div className="detail-metrics">
        <span><small>Supply share</small><strong>{node.percentage.toFixed(4)}%</strong></span>
        <span><small>Token balance</small><strong>{compactNumber(node.balance, 4)}</strong></span>
        <span><small>Address type</small><strong className="capitalize">{node.isHub ? "routing hub" : node.kind}</strong></span>
      </div>
      <div className="connection-head"><strong>Mapped connections</strong><span>{links.length}</span></div>
      <div className="connection-list">
        {links.map((link) => {
          const source = endpointId(link.source as unknown as string | GraphNode);
          const target = endpointId(link.target as unknown as string | GraphNode);
          const outgoing = source === node.id;
          const other = byId.get(outgoing ? target : source);
          return (
            <article key={link.id}>
              <span className={outgoing ? "outgoing" : "incoming"}>{outgoing ? <ArrowUpRight /> : <ArrowDownLeft />}</span>
              <span>
                {/* A traced counterparty has no holder rank, and "#?" reads as missing data. */}
                <strong>
                  {outgoing ? "Sent to" : "Received from"}{" "}
                  {other?.rank ? `#${other.rank}` : other?.label || (other?.kind && other.kind !== "wallet" ? other.kind : "traced wallet")}
                </strong>
                <small>{shortAddress(other?.address || "Unknown", 6)} · {link.transactions} transfer{link.transactions === 1 ? "" : "s"}</small>
              </span>
              <span className="connection-time">{relativeTime(link.lastTransfer)}</span>
              {link.hashes[0] && <a target="_blank" rel="noreferrer" href={explorerTransaction(data.token.chain, link.hashes[0])}><ExternalLink /></a>}
            </article>
          );
        })}
        {!links.length && <p>No direct transfers to another mapped holder were found in this sample.</p>}
      </div>
    </section>
  );
}
